import React, { useEffect, useState } from 'react';
import './styles/App.scss';
import MapComponent from './components/MapComponent';
import { clearAuthToken, getCurrentUser, hasAuthToken, login } from './services/api';

const isDesktopPublicPreview = typeof window !== 'undefined' && window.desktopConfig?.disableAuth;
const isPrivacyPolicyRoute = typeof window !== 'undefined' && window.location.pathname === '/privacy';

const getPrivacyPolicyHref = () => {
  if (typeof window === 'undefined') {
    return '/privacy';
  }

  if (window.location.protocol === 'file:') {
    return 'https://kaban.geohaiku.pl/privacy';
  }

  return '/privacy';
};

const getSupportHref = () => 'https://kaban.geohaiku.pl/';

const PrivacyPolicyPage = () => {
  const supportHref = getSupportHref();

  return (
    <div className="privacy-shell">
      <main className="privacy-card">
        <p className="privacy-kicker">Microsoft Store / privacy policy</p>
        <h1>Polityka prywatności</h1>

        <section>
          <h2>1. Administrator danych</h2>
          <p>
            Administrator danych: <strong>AirHaiku Piotr Polkowski</strong>
          </p>
          <p>
            Kontakt e-mail: <strong>info@geohaiku.pl</strong>
          </p>
        </section>

        <section>
          <h2>2. Jakie dane sa przetwarzane</h2>
          <ul>
            <li>GeoTIFFy wgrane przez uzytkownika.</li>
            <li>Dane o polach, obszarach szkody i zapisanym stanie aplikacji.</li>
            <li>Techniczne pliki lokalne potrzebne do dzialania aplikacji desktopowej.</li>
          </ul>
        </section>

        <section>
          <h2>3. Cel przetwarzania</h2>
          <p>
            Dane sa uzywane do lokalnej obslugi workflow szacowania szkód lowieckich, zapisu stanu pracy i obliczen geometrycznych.
          </p>
        </section>

        <section>
          <h2>4. Podstawa i zasieg</h2>
          <p>
            Aplikacja desktopowa dziala lokalnie na urzadzeniu uzytkownika. Dla podstawowego workflow nie jest wymagana osobna usluga chmurowa.
          </p>
          <p>
            Dane pozostaja na urzadzeniu do czasu usuniecia ich przez uzytkownika lub przez mechanizmy aplikacji.
          </p>
        </section>

        <section>
          <h2>5. Prawa uzytkownika</h2>
          <p>
            Mozesz poprosic o informacje o danych, ich poprawienie lub usuniecie, kontaktujac sie pod adresem e-mail podanym wyzej.
          </p>
        </section>

        <section>
          <h2>6. Kontakt i wsparcie</h2>
          <p>
            Strona wsparcia: <a href={supportHref}>{supportHref}</a>
          </p>
          <p>
            Wersja polityki prywatnosci moze byc aktualizowana wraz z rozwojem aplikacji.
          </p>
        </section>
      </main>
    </div>
  );
};

const LoginScreen = ({ onLogin, isLoading, errorMessage }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const privacyHref = getPrivacyPolicyHref();

  const handleSubmit = (event) => {
    event.preventDefault();
    onLogin(username, password);
  };

  return (
    <div className="auth-shell">
      <section className="auth-card">
        <h1>Szkody lowieckie</h1>
        <p>Zaloguj sie do systemu szacowania szkod lowieckich.</p>
        <form onSubmit={handleSubmit} className="auth-form">
          <input
            type="text"
            autoComplete="username"
            placeholder="Login"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={isLoading}
          />
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Haslo"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading || !username.trim() || !password}>
            {isLoading ? 'Logowanie...' : 'Zaloguj'}
          </button>
        </form>
        {errorMessage && <p className="auth-error">{errorMessage}</p>}
        <p className="auth-footer">
          <a href={privacyHref}>Polityka prywatnosci</a>
        </p>
      </section>
    </div>
  );
};

function AppContent() {
  const [authLoading, setAuthLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const bootstrapSession = async () => {
      if (isDesktopPublicPreview) {
        clearAuthToken();
        setAuthUser({
          username: 'preview',
          display_name: 'Tryb publiczny',
          role: 'operator',
          organization_name: 'Szkoła / las',
          capabilities: { can_edit_fields: true },
        });
        setAuthLoading(false);
        return;
      }

      if (!hasAuthToken()) {
        setAuthLoading(false);
        return;
      }

      try {
        const result = await getCurrentUser();
        setAuthUser(result?.user || null);
      } catch (error) {
        clearAuthToken();
      } finally {
        setAuthLoading(false);
      }
    };

    bootstrapSession();
  }, []);

  const handleLogin = async (username, password) => {
    try {
      setAuthError('');
      setLoginLoading(true);
      const result = await login(username.trim(), password);
      setAuthUser(result?.user || null);
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      setAuthError(backendMessage || 'Logowanie nie powiodlo sie.');
      clearAuthToken();
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    if (isDesktopPublicPreview) {
      return;
    }

    clearAuthToken();
    setAuthUser(null);
    setAuthError('');
  };

  if (authLoading) {
    return (
      <div className="auth-shell">
        <section className="auth-card">
          <p>Sprawdzanie sesji...</p>
        </section>
      </div>
    );
  }

  if (!authUser) {
    return <LoginScreen onLogin={handleLogin} isLoading={loginLoading} errorMessage={authError} />;
  }

  const canEditFields = Boolean(authUser.capabilities?.can_edit_fields);

  return (
    <div className="App">
      <header className="session-bar">
        {!isDesktopPublicPreview && (
          <div>
            <strong>{authUser.display_name || authUser.username}</strong>
            <span>{authUser.organization_name} • {authUser.role}</span>
          </div>
        )}
        <div className="session-bar__actions">
          <a className="session-bar__action-button" href={getPrivacyPolicyHref()}>Polityka prywatnosci</a>
          {!isDesktopPublicPreview && (
            <button type="button" onClick={handleLogout}>Wyloguj</button>
          )}
        </div>
      </header>
      <main>
        <MapComponent canEditFields={canEditFields} />
      </main>
    </div>
  );
}

function App() {
  if (isPrivacyPolicyRoute) {
    return <PrivacyPolicyPage />;
  }

  return <AppContent />;
}

export default App;
